import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserIcon, LockClosedIcon, TrashIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs'
import Button from '../../components/ui/Button'
import UsernameInput from '../../components/ui/UsernameInput'
import EmailInput from '../../components/ui/EmailInput'
import PhoneNumberInput from '../../components/ui/PhoneNumberInput'
import PasswordInput from '../../components/ui/PasswordInput'
import DeleteAccountModal from '../../components/ui/DeleteAccountModal'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useAuthStore } from '../../core/stores/authStore'
import { userService } from '../../core/services/user.service'
import toast from 'react-hot-toast'
import { UserRole } from '../../core/enums'

export default function SettingsPage() {
  const { t, isRTL } = useTranslation()
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const isBusinessOwner = user?.userRole === UserRole.BUSINESS_OWNER
  
  // @ts-ignore - updateUser exists in authStore, TypeScript cache issue
  const updateUser = useAuthStore((state) => state.updateUser)
  const clearAuth = useAuthStore((state) => state.clearAuth)

  // Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phoneNumber: ''
  })

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Loading states
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [isLoadingPassword, setIsLoadingPassword] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)

  // Initialize profile data from user
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || ''
      })
    }
  }, [user])

  const handleProfileUpdate = async () => {
    if (!user || !profileData.name) {
      toast.error(t('allFieldsRequired'))
      return
    }

    setIsLoadingProfile(true)
    try {
      const updatedUserProfile = await userService.updateProfile({
        name: profileData.name,
        email: profileData.email,
        phoneNumber: profileData.phoneNumber
      })

      // Update user in store - merge with existing user data
      if (user) {
        updateUser({
          ...user,
          name: updatedUserProfile.name,
          email: updatedUserProfile.email || '',
          phoneNumber: updatedUserProfile.phoneNumber
        })
        
        // Update profileData state immediately to reflect changes in the form
        setProfileData({
          name: updatedUserProfile.name,
          email: updatedUserProfile.email || '',
          phoneNumber: updatedUserProfile.phoneNumber
        })
      }
      toast.success(t('profileUpdatedSuccessfully'))
    } catch (error: any) {
      toast.error(error.message || t('failedToUpdateProfile'))
    } finally {
      setIsLoadingProfile(false)
    }
  }

  const handlePasswordChange = async () => {
    if (!user) return

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t('allFieldsRequired'))
      return
    }

    if (passwordData.newPassword.length < 8) {
      toast.error(t('passwordMustBeAtLeast8Characters'))
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('passwordsDoNotMatch'))
      return
    }

    setIsLoadingPassword(true)
    try {
      await userService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })

      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })

      toast.success(t('passwordChangedSuccessfully'))

      // Backend blacklists sessions after password change
      // Clear auth and redirect to login
      clearAuth()
      
      // Show message and redirect after a short delay
      setTimeout(() => {
        toast(t('pleaseLoginAgain') || 'Please login again with your new password', { icon: 'ℹ️' })
        navigate('/login', { replace: true })
      }, 1000)
    } catch (error: any) {
      toast.error(error.message || t('failedToChangePassword'))
    } finally {
      setIsLoadingPassword(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <p className="text-gray-700 mt-1">
          {t('businessSettingsDescription')}
        </p>
      </div>

      {/* Tabs Container */}
      <Tabs defaultTab="profile">
        <TabsList variant="horizontal">
          <TabsTrigger value="profile" icon={<UserIcon className="w-4 h-4" />}>
            {t('profileSettings')}
          </TabsTrigger>
          <TabsTrigger value="security" icon={<LockClosedIcon className="w-4 h-4" />}>
            {t('securitySettings')}
          </TabsTrigger>
          {isBusinessOwner && (
            <TabsTrigger value="notifications-data" icon={<Cog6ToothIcon className="w-4 h-4" />}>
              {t('deleteAccount')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          {/* Profile Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <UserIcon className="w-5 h-5 text-blue-600" />
                <CardTitle>{t('profileSettings')}</CardTitle>
              </div>
              <CardDescription>
                {t('profileSettingsDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <UsernameInput
                  label={t('fullName')}
                  value={profileData.name}
                  onChange={(value) => setProfileData({ ...profileData, name: value })}
                  required
                />
                <EmailInput
                  label={t('email')}
                  value={profileData.email}
                  onChange={(value) => setProfileData({ ...profileData, email: value })}
                />
                <div className="md:col-span-2">
                  <PhoneNumberInput
                    label={t('phoneNumber') || 'Phone Number'}
                    value={profileData.phoneNumber}
                    onChange={(value) => setProfileData({ ...profileData, phoneNumber: value })}
                    defaultCountryCode="+20"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleProfileUpdate}
                  isLoading={isLoadingProfile}
                  disabled={isLoadingProfile || !profileData.name}
                  className="bg-[#1E293B] text-white hover:bg-[#0F172A] active:bg-[#020617]"
                >
                  {t('save')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          {/* Security Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <LockClosedIcon className="w-5 h-5 text-red-600" />
                <CardTitle>{t('securitySettings')}</CardTitle>
              </div>
              <CardDescription>
                {t('securitySettingsDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <PasswordInput
                label={t('currentPassword')}
                value={passwordData.currentPassword}
                onChange={(value) => setPasswordData({ ...passwordData, currentPassword: value })}
                required
              />
              <PasswordInput
                label={t('newPassword')}
                value={passwordData.newPassword}
                onChange={(value) => setPasswordData({ ...passwordData, newPassword: value })}
                required
                helperText={t('passwordMinLength')}
              />
              <PasswordInput
                label={t('confirmPassword')}
                value={passwordData.confirmPassword}
                onChange={(value) => setPasswordData({ ...passwordData, confirmPassword: value })}
                required
              />
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handlePasswordChange}
                  isLoading={isLoadingPassword}
                  disabled={isLoadingPassword}
                  className="bg-[#1E293B] text-white hover:bg-[#0F172A] active:bg-[#020617]"
                >
                  {t('changePassword')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delete Account Tab */}
        {isBusinessOwner && (
          <TabsContent value="notifications-data">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <TrashIcon className="w-5 h-5 text-red-600" />
                  <CardTitle>{t('deleteAccount')}</CardTitle>
                </div>
              </CardTitle>
              <CardDescription>
                {t('confirmDeleteAccountMessageFull')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Warning Section */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                    <TrashIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 mb-2">
                      {t('deleteAccount')}
                    </h3>
                    <p className="text-sm text-red-800 whitespace-pre-line">
                      {t('confirmDeleteAccountMessageFull')}
                    </p>
                  </div>
                </div>
              </div>

              {/* User Info */}
              {user && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('profileSettings')}</h4>
                  <table className="w-full text-sm border-collapse">
                    <tbody>
                      <tr>
                        <td className="text-gray-600 py-2">{t('fullName')}:</td>
                        <td className="font-medium text-gray-900 py-2">{user.name || '-'}</td>
                      </tr>
                      {user.email && (
                        <tr>
                          <td className="text-gray-600 py-2">{t('email')}:</td>
                          <td className="font-medium text-gray-900 py-2">{user.email}</td>
                        </tr>
                      )}
                      {user.phoneNumber && (
                        <tr>
                          <td className="text-gray-600 py-2">{t('phoneNumber')}:</td>
                          <td className="font-medium text-gray-900 py-2 font-mono text-right" dir="ltr">{user.phoneNumber}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Delete Button */}
              <div className="pt-4 border-t border-gray-200 flex justify-end">
                <Button
                  onClick={() => {
                    setIsDeletingAccount(true)
                  }}
                  variant="danger"
                  size="md"
                  className="bg-red-600 text-white hover:bg-red-700 active:bg-red-800 border-0"
                >
                  {t('deleteAccount')}
                </Button>
              </div>
            </CardContent>
          </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={isDeletingAccount}
        onClose={() => {
          setIsDeletingAccount(false)
        }}
        onConfirm={async () => {
          if (!user) return
          
          try {
            await userService.deleteAccount()
            toast.success(t('accountDeletedSuccessfully'))
            
            // Clear auth and logout
            clearAuth()
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            
            // Redirect to home after a short delay
            setTimeout(() => {
              navigate('/', { replace: true })
            }, 2000)
          } catch (error: any) {
            // Error message is already displayed by axios interceptor
            console.error('Delete account error:', error)
          }
        }}
        user={user}
        title={t('deleteAccount')}
        description={t('confirmDeleteAccountMessageFull')}
        actionButtonText={t('deleteAccount')}
        isLoading={false}
      />
    </div>
  )
}

